import {snowplowTrackEvent} from "utils/snowplow";
import UTIF from "utif";
import * as Api from 'utils/api/api.js';
import {IMAGE_MIME_TYPE, UID_TYPES} from "utils/constants";


const HTML_MIME_TYPE = "text/html";
const APPLICATION_HTML_MIME_TYPE = "application/html";
const TIFF_MIME_TYPE = "image/tiff";

type MimeKind = "pdf" | "html" | "image" | "other";

const getMimeKind = (mimeType: string | null | undefined): MimeKind => {
    const type = (mimeType || "").split(";")[0].trim().toLowerCase();
    if (type === "application/pdf") return "pdf";
    if (type === HTML_MIME_TYPE || type === APPLICATION_HTML_MIME_TYPE) return "html";
    if (type.startsWith(IMAGE_MIME_TYPE)) return "image";
    return "other";
}

export const isMimeTypeMismatch = (declaredMimeType: string | null | undefined, actualMimeType: string | null | undefined): boolean => {
    const actualKind = getMimeKind(actualMimeType);
    return !!declaredMimeType && actualKind !== "other" && actualKind !== getMimeKind(declaredMimeType);
}

const getImageSizeFromUrl = (
    url: string
): Promise<{ height: number; width: number }> => {
    return new Promise((resolve, reject) => {
        // Use the global Image constructor, not the React component
        const img = new window.Image();

        img.onload = () => {
            resolve({height: img.naturalHeight || img.height, width: img.naturalWidth || img.width});
        };

        img.onerror = (err) => {
            reject(err);
        };

        img.src = url;
    });
};

type AttachmentResult = string | null | {url: string, height: number, width: number};

// asks the attachments api that serves this kind of uid for the storage urls; null when none does
const getAttachmentUrls = (uid: string, docVersion: string | number | null, docId: Array<number> | number | string) => {
    if (uid.includes(UID_TYPES.TASK)) {
        return Api.getTaskAttachments(uid, docId);
    }
    if (uid.includes(UID_TYPES.DOCUMENT)) {
        return Api.getDocumentAttachments(uid, docId, docVersion);
    }
    if (uid.includes(UID_TYPES.PROCESS)) {
        return Api.getProcessAttachments(uid, docId);
    }
    return null;
}

// turns the stored blob into what the viewers draw: decoded tiff pixels, an image url with its size, or an object url
const toAttachmentResult = async (attachmentBlob: Blob, mimeType: string): Promise<AttachmentResult> => {
    if (isMimeTypeMismatch(mimeType, attachmentBlob.type)) {
        throw new Error(`Attachment declared as ${mimeType} but storage returned ${attachmentBlob.type}`);
    }
    if (attachmentBlob.type === TIFF_MIME_TYPE) {
        let arrayBufferTiff = await attachmentBlob.arrayBuffer();
        let decodedTiff = UTIF.decode(arrayBufferTiff);
        UTIF.decodeImage(arrayBufferTiff, decodedTiff[0]);
        let imageObject = await UTIF.toRGBA8(decodedTiff[0]);
        imageObject.width = decodedTiff[0].width;
        imageObject.height = decodedTiff[0].height;

        return imageObject;
    }

    const encodedBlob = (mimeType === HTML_MIME_TYPE || mimeType === APPLICATION_HTML_MIME_TYPE) ? new Blob([attachmentBlob], {type: HTML_MIME_TYPE + ';charset=utf-8'}) : attachmentBlob;
    const objectURL = URL.createObjectURL(encodedBlob);

    if (attachmentBlob.type.includes(IMAGE_MIME_TYPE)) {
        const sizeResponse = await getImageSizeFromUrl(objectURL);

        return {url: objectURL, height: sizeResponse.height, width: sizeResponse.width};
    }
    return objectURL;
}

export async function fetchAttachmentBlob(url: string, mimeType: string, userData: any, uid: string | null, docVersion: string | number | null, docId: Array<number> | number | string): Promise<AttachmentResult> {

    let result: AttachmentResult = "";
    let startTime = new Date().getTime();

    try {
        if (!uid) {
            return null;
        }
        const urlsRequest = getAttachmentUrls(uid, docVersion, docId);
        if (urlsRequest === null) {
            // no attachments api serves this kind of uid, so there is no storage url to fetch
            return null;
        }
        const approvalResponse = await urlsRequest;
        if (approvalResponse?.urls[0].error) {
            throw new Error(`Error fetching attachment URL: ${approvalResponse?.urls[0].error}`);
        }

        let apiStorageUrl = getUrlWithCorsParameter(approvalResponse?.urls[0].url);

        let dataStorageReply = await fetch(apiStorageUrl);
        const attachmentBlob: Blob | undefined = await dataStorageReply?.blob();

        if (attachmentBlob) {
            result = await toAttachmentResult(attachmentBlob, mimeType);
        }

    } catch (error) {
        if (error instanceof Error)
            console.log("Could not get the attachments url " + error.message);
        throw error;
    }
    let stopTime = new Date().getTime();
    let total = ((stopTime - startTime) / 1000).toString() || "unknown";

    snowplowTrackEvent({
        category: "bypass-cir",
        action: "fetch-attachment",
        label: "fetch-attachment",
        value: total
    }, userData);

    return result;
}

function getUrlWithCorsParameter(url: string | undefined): string | undefined {
    let apiStorageUrl = url;
    if (typeof apiStorageUrl === "string") {
        try {
            const hostname = new URL(apiStorageUrl).hostname;
            if (hostname.endsWith(".vismaonline.com")) {
                apiStorageUrl += "?corsOrigin=" + encodeURIComponent(window.location.origin);
            }
        } catch (e) {
            // Invalid URL, do nothing
        }
    }
    return apiStorageUrl;
}

export default fetchAttachmentBlob
